import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Card } from 'react-bootstrap';
import './VoterDetails.css';

const AVATAR_TONES = ['purple', 'pink', 'green', 'blue', 'orange'];

const getInitials = (firstName = '', lastName = '') => {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return 'NA';
};

const avatarTone = (id = '') => {
  const hash = String(id).split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

const displayValue = (value) => {
  if (value == null || String(value).trim() === '') return 'N/A';
  return value;
};

const statusMeta = (status = '') => {
  const key = String(status || '').toLowerCase();
  if (key === 'completed' || key === 'survey') {
    return { label: 'Completed', tone: 'completed', icon: 'bi-check-circle-fill' };
  }
  if (key === 'unreachable') {
    return { label: 'Unreachable', tone: 'unreachable', icon: 'bi-telephone-x-fill' };
  }
  if (key === 'call_back' || key === 'callback') {
    return { label: 'Call Back', tone: 'callback', icon: 'bi-telephone-fill' };
  }
  return { label: status || 'Pending', tone: 'pending', icon: 'bi-circle' };
};

const formatTimelineDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const timelineDescription = (answer) => {
  const type = String(answer?.type || 'survey').toLowerCase();
  if (answer?.answerText) return answer.answerText;
  if (type === 'unreachable') return 'No response';
  if (type === 'callback' || type === 'call_back') return 'Requested to call later';
  if (answer?.question?.questionText) return answer.question.questionText;
  return 'Spoke with voter';
};

const DetailRow = ({ label, value }) => (
  <div className="vd-row">
    <span>{label}</span>
    <strong>{displayValue(value)}</strong>
  </div>
);

const VoterDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const group = state;

  if (!group) {
    return <div className="p-4">No Data Found</div>;
  }

  const voter = group.voter || {};
  const answers = Array.isArray(group.answers) ? group.answers : [];
  const headerStatus = statusMeta(voter.callStatus);
  const initials = getInitials(voter.firstName, voter.lastName);
  const fullName = [voter.firstName, voter.lastName].filter(Boolean).join(' ') || 'N/A';

  const timeline = [...answers]
    .sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt))
    .map((answer) => {
      const status = statusMeta(answer.type || voter.callStatus);
      return {
        key: `${answer.recordedAt}-${answer.type}-${answer.answerText}`,
        dateLabel: formatTimelineDate(answer.recordedAt),
        status,
        description: timelineDescription(answer)
      };
    });

  return (
    <div className="vd-page">
      <Container fluid className="vd-container">
        <div className="vd-topbar">
          <button type="button" className="vd-back" onClick={() => navigate(-1)} aria-label="Back">
            <i className="bi bi-arrow-left" />
          </button>
          <h1>Voter Details</h1>
        </div>

        <Card className="vd-card vd-top-card">
          <Card.Body>
            <div className="vd-summary">
              <div className="vd-identity">
                <div className={`vd-avatar vd-avatar-${avatarTone(voter._id)}`}>{initials}</div>
                <div>
                  <div className="vd-name">{fullName}</div>
                  <div className="vd-epic">Election Card No. {displayValue(voter.epicNo)}</div>
                </div>
              </div>
              <span className={`vd-status vd-status-${headerStatus.tone}`}>
                <i className={`bi ${headerStatus.icon}`} aria-hidden="true" />
                {headerStatus.label}
              </span>
            </div>
          </Card.Body>
        </Card>

        <div className="vd-stat-grid">
          <Card className="vd-card vd-stat-card">
            <Card.Body>
              <div className="vd-stat-icon is-blue"><i className="bi bi-building" /></div>
              <div className="vd-stat-copy">
                <div className="vd-stat-value">{displayValue(voter.acNo)}</div>
                <div className="vd-stat-label">Assembly No</div>
              </div>
            </Card.Body>
          </Card>
          <Card className="vd-card vd-stat-card">
            <Card.Body>
              <div className="vd-stat-icon is-purple"><i className="bi bi-geo-alt" /></div>
              <div className="vd-stat-copy">
                <div className="vd-stat-value">{displayValue(voter.acName)}</div>
                <div className="vd-stat-label">Assembly Name</div>
              </div>
            </Card.Body>
          </Card>
          <Card className="vd-card vd-stat-card">
            <Card.Body>
              <div className="vd-stat-icon is-green"><i className="bi bi-house" /></div>
              <div className="vd-stat-copy">
                <div className="vd-stat-value">{displayValue(voter.booth)}</div>
                <div className="vd-stat-label">Booth No</div>
              </div>
            </Card.Body>
          </Card>
          <Card className="vd-card vd-stat-card">
            <Card.Body>
              <div className="vd-stat-icon is-orange"><i className="bi bi-map" /></div>
              <div className="vd-stat-copy">
                <div className="vd-stat-value">{displayValue(voter.ward)}</div>
                <div className="vd-stat-label">Ward No</div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="vd-detail-grid">
          <Card className="vd-card">
            <Card.Body>
              <h2 className="vd-section-title"><i className="bi bi-person" /> Personal Details</h2>
              <DetailRow label="Full Name" value={fullName} />
              <DetailRow label="Mobile Number" value={voter.phone} />
              <DetailRow label="EPIC No" value={voter.epicNo} />
            </Card.Body>
          </Card>

          <Card className="vd-card">
            <Card.Body>
              <h2 className="vd-section-title"><i className="bi bi-geo-alt" /> Polling Station</h2>
              <DetailRow label="Polling Station" value={voter.pollingStationName || voter.engMathak} />
              <DetailRow label="Address" value={voter.pollingStationAddress} />
            </Card.Body>
          </Card>

          <Card className="vd-card">
            <Card.Body>
              <h2 className="vd-section-title"><i className="bi bi-house-door" /> Residence Details</h2>
              <DetailRow label="Society Name" value={voter.societyName} />
              <DetailRow label="House No" value={voter.houseNo} />
              <DetailRow label="Eng. House No" value={voter.engHouseNo} />
              <DetailRow label="Res. No" value={voter.resNo} />
              <DetailRow label="Eng Address" value={voter.engAddress || voter.address} />
            </Card.Body>
          </Card>
        </div>

        <Card className="vd-card vd-timeline-card">
          <Card.Body>
            <h2 className="vd-section-title is-timeline"><i className="bi bi-clock-history" /> Interaction Timeline</h2>
            {timeline.length === 0 ? (
              <div className="vd-timeline-empty">No interaction history available.</div>
            ) : (
              <div className="vd-timeline">
                {timeline.map((item) => (
                  <div key={item.key} className={`vd-timeline-item is-${item.status.tone}`}>
                    <div className="vd-timeline-icon">
                      <i className={`bi ${item.status.icon}`} />
                    </div>
                    <div className="vd-timeline-copy">
                      <div className="vd-timeline-date">{item.dateLabel}</div>
                      <div className="vd-timeline-status">{item.status.label}</div>
                      <div className="vd-timeline-desc">{item.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default VoterDetails;
